/**
 * Screen dive: a paid-social ad made from real phone footage. The clip plays as it was filmed (a hand,
 * the app, the sound) and at set moments the camera dives into the phone: the screen becomes the live
 * Stippl map of the very trip on it, then the camera pulls back out (or the film cuts on the beat).
 *
 * Everything that changes per ad is a prop, so one composition serves every clip:
 * - `base`: the filmed clip (9:16 fits; another shape is cropped to fill the canvas, as objectFit "cover" does) and
 *   `baseSize`, the pixel size its screen measurements are in;
 * - `dives`: when each dive starts and ends, where the phone's screen sits on the frame (a rotated
 *   rectangle, `quad`), and what the map shows (a stop's places, or the route of the trip);
 * - `captions`: our own captions, fixed on screen while the frame moves under them.
 *
 * A dive runs on a still of the phone: the clip frozen at the dive's first frame, or an image passed
 * as `stillIn` (for an older clip whose captions are burned in: paint them out of the still, see the
 * `ad-video` skill, and set the caption again in `captions`). Media values are https URLs on the
 * render box, or paths in the public dir for a local render (see ../kit/assets.ts).
 */
import React, { useState } from "react";
import { AbsoluteFill, Audio, Easing, Freeze, Img, OffthreadVideo, Sequence, useCurrentFrame } from "remotion";
import { loadFont as loadMontserrat } from "@remotion/google-fonts/Montserrat";
import { fontFamily as EMOJI_FAMILY, loadFont as loadEmoji } from "@remotion/google-fonts/NotoColorEmoji";
import { mediaSrc } from "../kit/assets";
import { BODY, C, HEAD, RESET_ATTR, RESET_CSS } from "../kit/brand";
import { eio, evenDots, mix, ramp } from "../kit/kit";
import { FILL, LegIcon, PhotoMarker, type MediaSrc } from "../kit/markers";
import { MapStage, type Project } from "../kit/MapStage";
import { emojiSubsets } from "./emojiSubsets";

const { fontFamily: MONT } = loadMontserrat("normal", { weights: ["700"], subsets: ["latin"] });

export const AD_W = 1080;
export const AD_H = 1920;
export const AD_FPS = 30;

// the ads' own ease-out, softer than the kit's `eo`; the approved render uses it, so every default-eased ramp here passes it
const AD_EASE = Easing.bezier(0.22, 1, 0.36, 1);
const lin = (x: number) => x;
// the emoji font's name, quoted for CSS (it has spaces); loaded by ScreenDive for the captions that use it
const EMOJI = `"${EMOJI_FAMILY}"`;
// the angle each stop's photo card rests at; the card that opens into the clip starts from it
const STOP_ROT = [-3, 4, -4, 3, 5, -3];

export type LngLat = [number, number];

/** The phone's display on a frame: centre, size and tilt (degrees, clockwise), in `baseSize` pixels */
export type Quad = { cx: number; cy: number; w: number; h: number; rot: number };

export type MapCamera = {
	lng: number;
	lat: number;
	zoom: number;
	/** the zoom it drifts to over the dive (a slow push in or pull back) */
	zoomTo?: number;
	pitch: number;
	bearing: number;
	/** room kept free at the top (the caption) and the bottom (a card), in output pixels */
	padTop?: number;
	padBottom?: number;
};

export type DivePlace = {
	name: string;
	ll: LngLat;
	photo?: string;
	glyph?: string;
	fill?: keyof typeof FILL;
	/** card sideways, in card widths (-0.6 left, 0.6 right) when two places sit close */
	cardDx?: number;
	captionSide?: -1 | 0 | 1;
};

export type DiveStop = { name: string; ll: LngLat; nights: number; photo?: string; cardDx?: number; captionSide?: -1 | 0 | 1 };

export type DiveScene =
	| {
			kind: "places";
			camera: MapCamera;
			places: DivePlace[];
			/** the stop's card under the map, as in the app */
			stopCard?: { title: string; dates: string; nights: number };
	  }
	| {
			kind: "route";
			camera: MapCamera;
			stops: DiveStop[];
			/** one per leg: a glyph (plane, boat, bus, train) or null when no transport is chosen */
			legs: (string | null)[];
			/** a stop's photo card opens into a clip, full screen, at frame `at` (the film usually cuts on it) */
			open?: { stop: number; clip: string; at: number };
	  };

export type Dive = {
	/** first and last frame the dive layer is on screen */
	from: number;
	end: number;
	/** frames of the zoom into the screen */
	dive: [number, number];
	/** frames of the pull back out; leave it out to stay in until `end` (a cut on the beat) */
	back?: [number, number];
	quadIn: Quad;
	/** where the screen is when the camera comes back out (the hand has moved) */
	quadOut?: Quad;
	/** the frame to dive on; default: the clip frozen at `from` */
	stillIn?: string;
	stillOut?: string;
	scene: DiveScene;
};

export type AdCaption = { text: string; from: number; to: number; emoji?: string };

/** the word under a stop's nights, for the ad's language */
export type NightLabel = { one: string; other: string };
const NIGHTS: NightLabel = { one: "night", other: "nights" };
const nightsOf = (n: number, label: NightLabel) => (n === 1 ? label.one : label.other);

export type ScreenDiveProps = {
	base: string;
	baseSize: { w: number; h: number };
	durationInFrames: number;
	/** the clip's own sound: 1 keeps it, 0 mutes it (then pass music) */
	baseVolume?: number;
	music?: { src: string; volume?: number; fromSeconds?: number };
	captions?: AdCaption[];
	/** where the captions sit, centre line in output pixels */
	captionY?: number;
	dives: Dive[];
	/** the word for 1 night and for several, e.g. "nacht" / "nachten"; default "night" / "nights" */
	nightLabel?: NightLabel;
	/** debug: the dives without the map */
	noMap?: boolean;
};

// ---------- the dive ----------
// where the clip sits on the canvas: drawn objectFit "cover", so scaled by k and centred; a quad's measurements are in clip pixels
type Cover = { k: number; ox: number; oy: number };

const DiveLayer: React.FC<{ d: Dive; base: string; cover: Cover; children: React.ReactNode }> = ({ d, base, cover, children }) => {
	const { k, ox, oy } = cover;
	const f = useCurrentFrame();
	if (f < d.from || f >= d.end) return null;
	const out = !!d.back && f >= d.back[0];
	const q = out && d.quadOut ? d.quadOut : d.quadIn;
	const still = out ? d.stillOut : d.stillIn;
	const p = out ? 1 - ramp(f, d.back![0], d.back![1], eio) : ramp(f, d.dive[0], d.dive[1], eio);
	const S = AD_W / (q.w * k);
	const s = Math.pow(S, p);
	const cx = q.cx * k + ox;
	const cy = q.cy * k + oy;
	// the screen turns into the map while the camera starts to move
	const alive = ramp(p, 0.04, 0.38, lin);
	// hand over to and from the running clip on the first and last frames
	const o = out ? 1 - ramp(f, d.end - 3, d.end, lin) : ramp(f, d.from, d.from + 3, lin);
	const frameAt = out ? d.back![1] : d.from;
	return (
		<AbsoluteFill style={{ opacity: o, zIndex: 30, overflow: "hidden" }}>
			<div
				style={{
					position: "absolute",
					left: 0,
					top: 0,
					width: AD_W,
					height: AD_H,
					transformOrigin: `${cx}px ${cy}px`,
					transform: `translate(${(AD_W / 2 - cx) * p}px, ${(AD_H / 2 - cy) * p}px) rotate(${-q.rot * p}deg) scale(${s})`,
				}}
			>
				{still ? (
					<Img src={mediaSrc(still)} style={{ position: "absolute", left: 0, top: 0, width: AD_W, height: AD_H, objectFit: "cover" }} />
				) : (
					<Freeze frame={frameAt}>
						<OffthreadVideo src={mediaSrc(base)} muted style={{ position: "absolute", left: 0, top: 0, width: AD_W, height: AD_H, objectFit: "cover" }} />
					</Freeze>
				)}
				<div
					style={{
						position: "absolute",
						left: cx - (q.w * k) / 2,
						top: cy - (q.h * k) / 2,
						width: q.w * k,
						height: q.h * k,
						transform: `rotate(${q.rot}deg)`,
						borderRadius: 34 * k,
						overflow: "hidden",
						opacity: alive,
					}}
				>
					<div
						style={{
							position: "absolute",
							left: 0,
							top: (q.h * k - AD_H / S) / 2,
							width: AD_W,
							height: AD_H,
							transform: `scale(${1 / S})`,
							transformOrigin: "0 0",
						}}
					>
						{children}
					</div>
				</div>
			</div>
		</AbsoluteFill>
	);
};

// ---------- captions, in the native style, fixed while the frame moves ----------
const Captions: React.FC<{ captions: AdCaption[]; y: number }> = ({ captions, y }) => {
	const f = useCurrentFrame();
	const c = captions.find((x) => f >= x.from && f < x.to);
	if (!c) return null;
	const vis = Math.min(ramp(f, c.from, c.from + 3, lin), 1 - ramp(f, c.to - 3, c.to, lin));
	return (
		<div
			style={{
				position: "absolute",
				left: 0,
				right: 0,
				top: y - 36,
				height: 72,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: 22,
				opacity: vis,
				zIndex: 80,
			}}
		>
			{c.emoji ? <span style={{ fontSize: 60, lineHeight: 1, fontFamily: EMOJI }}>{c.emoji}</span> : null}
			<span
				style={{
					font: `700 62px ${MONT}, ${EMOJI}`,
					letterSpacing: "-0.01em",
					color: "#fff",
					WebkitTextStroke: "11px #000",
					paintOrder: "stroke fill",
					lineHeight: 1,
				}}
			>
				{c.text}
			</span>
			{c.emoji ? <span style={{ fontSize: 60, lineHeight: 1, fontFamily: EMOJI }}>{c.emoji}</span> : null}
		</div>
	);
};

const photo = (src: string): MediaSrc => ({ src });

// ---------- scene: a stop's places, with its card ----------
const StopCard: React.FC<{ at: number; card: { title: string; dates: string; nights: number }; label: NightLabel }> = ({ at, card, label }) => {
	const f = useCurrentFrame();
	const t = ramp(f, at, at + 14, AD_EASE);
	if (t <= 0) return null;
	return (
		<div style={{ position: "absolute", left: 90, top: 1560 + (1 - t) * 60, width: 900, opacity: t, zIndex: 50 }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 22,
					padding: "26px 28px 26px 34px",
					background: "rgba(255,255,255,.97)",
					borderRadius: 36,
					boxShadow: "0 24px 60px rgba(0,0,0,.28)",
				}}
			>
				<div style={{ flex: 1 }}>
					<div style={{ font: `600 40px ${HEAD}`, color: C.ink }}>{card.title}</div>
					<div style={{ font: `400 26px ${BODY}`, color: "#658C96", marginTop: 4 }}>{card.dates}</div>
				</div>
				<span style={{ width: 70, height: 70, borderRadius: 99, border: "2px solid #DDE9ED", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<svg width={26} height={26} viewBox="0 0 24 24">
						<path d="M5 12h14" stroke={C.ink} strokeWidth={2.4} strokeLinecap="round" />
					</svg>
				</span>
				<span style={{ width: 80, textAlign: "center" }}>
					<div style={{ font: `600 44px ${HEAD}`, color: C.ink, lineHeight: 1 }}>{card.nights}</div>
					<div style={{ font: `500 19px ${HEAD}`, color: C.ink }}>{nightsOf(card.nights, label)}</div>
				</span>
				<span style={{ width: 70, height: 70, borderRadius: 99, background: C.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<svg width={26} height={26} viewBox="0 0 24 24">
						<path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
					</svg>
				</span>
			</div>
		</div>
	);
};

const camAt = (cam: MapCamera, g: number, span: number, e: (x: number) => number = lin) => ({
	lng: cam.lng,
	lat: cam.lat,
	zoom: cam.zoomTo === undefined ? cam.zoom : mix(ramp(g, 0, span, e), cam.zoom, cam.zoomTo),
	pitch: cam.pitch,
	bearing: cam.bearing,
});

const PlacesScene: React.FC<{ g0: number; scene: Extract<DiveScene, { kind: "places" }>; label: NightLabel }> = ({ g0, scene, label }) => {
	const f = useCurrentFrame();
	const g = f - g0;
	const R = 40;
	const cs = Math.round(R * 3.9);
	const overlay = (project: Project) =>
		scene.places.map((pl, i) => {
			const p = project(pl.ll[0], pl.ll[1]);
			const at = 14 + i * 5;
			return (
				<PhotoMarker
					key={pl.name}
					x={p.x}
					y={p.y}
					R={R}
					fill={FILL[pl.fill ?? "outdoors"]}
					glyph={pl.glyph ?? "star"}
					media={pl.photo ? photo(pl.photo) : undefined}
					mediaFrom={g0 + at}
					rot={[-4, 3, -3, 4, -2, 2][i % 6]}
					markerT={ramp(g, at, at + 9, AD_EASE)}
					cardT={ramp(g, at + 3, at + 15, AD_EASE)}
					captionT={ramp(g, at + 6, at + 16, AD_EASE)}
					caption={pl.name}
					bob={i}
					z={8}
					cardDx={(pl.cardDx ?? 0) * cs}
					captionSide={pl.captionSide ?? 0}
				/>
			);
		});
	return (
		<AbsoluteFill style={{ background: C.mint }}>
			<MapStage
				w={AD_W}
				h={AD_H}
				cam={camAt(scene.camera, g, 70)}
				padding={{ top: scene.camera.padTop ?? 700, bottom: scene.camera.padBottom ?? 350, left: 0, right: 0 }}
				overlay={overlay}
			/>
			{scene.stopCard ? <StopCard at={g0 + 18} card={scene.stopCard} label={label} /> : null}
		</AbsoluteFill>
	);
};

// ---------- scene: the route of the trip, stop by stop ----------
const legCurve = (a: LngLat, b: LngLat, bend: number, n = 40): LngLat[] => {
	const cx = (a[0] + b[0]) / 2 - (b[1] - a[1]) * bend;
	const cy = (a[1] + b[1]) / 2 + (b[0] - a[0]) * bend;
	return Array.from({ length: n + 1 }, (_, j) => {
		const u = j / n;
		return [(1 - u) * (1 - u) * a[0] + 2 * (1 - u) * u * cx + u * u * b[0], (1 - u) * (1 - u) * a[1] + 2 * (1 - u) * u * cy + u * u * b[1]] as LngLat;
	});
};

const RouteScene: React.FC<{ g0: number; scene: Extract<DiveScene, { kind: "route" }>; label: NightLabel }> = ({ g0, scene, label }) => {
	const f = useCurrentFrame();
	const g = f - g0;
	const R = 38;
	const cs = Math.round(R * 3.9);
	const POPG = (i: number) => 14 + i * 6;
	const legs = scene.stops.slice(1).map((s, i) => legCurve(scene.stops[i].ll, s.ll, [0.18, -0.15, 0.2, -0.22][i % 4]));
	const openAt = scene.open?.at ?? Infinity;
	const open = scene.open ? ramp(f, openAt, openAt + 12, eio) : 0;
	const overlay = (project: Project) => {
		const pts = scene.stops.map((s) => project(s.ll[0], s.ll[1]));
		const lines = legs.map((l) =>
			l.map(([lng, lat]) => {
				const p = project(lng, lat);
				return [p.x, p.y] as [number, number];
			})
		);
		const float = (i: number) => Math.sin((f + i * 17) / 22) * R * 0.08;
		const o = scene.open?.stop ?? -1;
		const card = o >= 0 ? { x: pts[o].x + (scene.stops[o].cardDx ?? 0) * cs, y: pts[o].y - R * 0.62 - cs / 2 + float(o) } : null;
		return (
			<>
				<svg style={{ position: "absolute", inset: 0, overflow: "visible" }} width={AD_W} height={AD_H}>
					{lines.map((l, s) => {
						const rv = ramp(g, POPG(s) + 3, POPG(s + 1), eio);
						return evenDots(l, 13)
							.filter((d) => rv > 0 && d.p <= rv)
							.map((d, i) => <circle key={`${s}-${i}`} cx={d.x} cy={d.y} r={4.5} fill="#557177" />);
					})}
				</svg>
				{lines.map((l, s) => {
					const gl = scene.legs[s];
					if (!gl) return null;
					const [x, y] = l[Math.floor(l.length / 2)];
					return <LegIcon key={s} x={x} y={y} size={50} glyph={gl} t={ramp(g, POPG(s + 1) - 4, POPG(s + 1) + 6, AD_EASE)} />;
				})}
				{scene.stops.map((s, i) => (
					<PhotoMarker
						key={s.name}
						x={pts[i].x}
						y={pts[i].y}
						R={R}
						fill={FILL.city}
						num={i + 1}
						media={s.photo ? photo(s.photo) : undefined}
						mediaFrom={g0 + POPG(i)}
						rot={STOP_ROT[i % 6]}
						markerT={ramp(g, POPG(i), POPG(i) + 9, AD_EASE)}
						cardT={i === o && open > 0 ? 0 : ramp(g, POPG(i) + 3, POPG(i) + 15, AD_EASE)}
						captionT={ramp(g, POPG(i) + 5, POPG(i) + 15, AD_EASE)}
						caption={`${s.name} · ${s.nights} ${nightsOf(s.nights, label)}`}
						bob={i}
						z={8}
						cardDx={(s.cardDx ?? 0) * cs}
						captionSide={s.captionSide ?? 0}
					/>
				))}
				{card && scene.open && open > 0 ? (
					<div
						style={{
							position: "absolute",
							left: mix(open, card.x - cs / 2, 0),
							top: mix(open, card.y - cs / 2, 0),
							width: mix(open, cs, AD_W),
							height: mix(open, cs, AD_H),
							borderRadius: mix(open, 16 * (R / 23), 0),
							border: `${mix(open, 3 * (R / 23), 0)}px solid #fff`,
							boxSizing: "border-box",
							overflow: "hidden",
							transformOrigin: "50% 100%",
							transform: `rotate(${mix(open, STOP_ROT[o % 6], 0)}deg)`,
							zIndex: 90,
						}}
					>
						<Sequence from={openAt} layout="none">
							<OffthreadVideo src={mediaSrc(scene.open.clip)} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
						</Sequence>
					</div>
				) : null}
			</>
		);
	};
	return (
		<AbsoluteFill style={{ background: C.mint }}>
			<MapStage
				w={AD_W}
				h={AD_H}
				cam={camAt(scene.camera, g - 10, 42, eio)}
				padding={{ top: scene.camera.padTop ?? 780, bottom: scene.camera.padBottom ?? 110, left: 0, right: 0 }}
				overlay={overlay}
			/>
		</AbsoluteFill>
	);
};

export const ScreenDive: React.FC<ScreenDiveProps> = ({
	base,
	baseSize,
	baseVolume = 1,
	music,
	captions = [],
	captionY = 603,
	dives,
	nightLabel = NIGHTS,
	noMap = false,
}) => {
	// The box has no emoji font, so load Noto Color Emoji. Only the subsets these captions use: this module is in the bundle
	// every trip-movie render loads, and the full font is ten files. Once per mount; the loader holds the render until they arrive.
	useState(() => {
		const subsets = emojiSubsets(captions.map((c) => `${c.text}${c.emoji ?? ""}`).join(""));
		// Remotion's types only name the "emoji" umbrella, the loader takes the unicode-range keys of the font's own info
		if (subsets.length) loadEmoji("normal", { weights: ["400"], subsets: subsets as "emoji"[] });
	});
	const k = Math.max(AD_W / baseSize.w, AD_H / baseSize.h);
	const cover = { k, ox: (AD_W - baseSize.w * k) / 2, oy: (AD_H - baseSize.h * k) / 2 };
	return (
		<AbsoluteFill {...RESET_ATTR} style={{ background: "#000" }}>
			<style>{RESET_CSS}</style>
			<OffthreadVideo src={mediaSrc(base)} volume={baseVolume} style={{ width: AD_W, height: AD_H, objectFit: "cover" }} />
			{music ? <Audio src={mediaSrc(music.src)} volume={music.volume ?? 0.8} trimBefore={Math.round((music.fromSeconds ?? 0) * AD_FPS)} /> : null}
			{dives.map((d, i) => (
				<DiveLayer key={i} d={d} base={base} cover={cover}>
					{noMap ? null : d.scene.kind === "places" ? (
						<PlacesScene g0={d.from} scene={d.scene} label={nightLabel} />
					) : (
						<RouteScene g0={d.from} scene={d.scene} label={nightLabel} />
					)}
				</DiveLayer>
			))}
			<Captions captions={captions} y={captionY} />
		</AbsoluteFill>
	);
};
